import React, { useState, useEffect } from 'react';
import { logUserActivityQuietly } from './supabaseClient';

export default function ParacetamolCalculator({user}) {
  // --- STATE HOOKS FOR MODEL INPUTS ---
  const [overdoseType, setOverdoseType] = useState('acute'); // 'acute' or 'chronic'
  const [hoursPostIngestion, setHoursPostIngestion] = useState(''); // Used for acute tracking
  const [measuredLevel, setMeasuredLevel] = useState('');
  const [unit, setUnit] = useState('mg/L'); 
  const [weight, setWeight] = useState(''); 
  const [isHighRiskPatient, setIsHighRiskPatient] = useState(false); // High risk of hepatotoxicity flag

  // --- STATE HOOK FOR CLINICAL EVALUATION PANELS ---
  const [triage, setTriage] = useState({
    statusLabel: 'AWAITING METRICS',
    color: '#7f8c8d',
    bgColor: 'rgba(127, 140, 141, 0.1)',
    normalizedMgL: 0,
    thresholdMgL: 0,
    isToxic: false,
    nacDosingPlan: [], 
    actionPlan: 'Provide patient clinical parameters to map dynamic toxicological guidelines.'
  });

  // Reusable Styling Specifications
  const cardStyle = { background: '#1c2226', padding: '20px', borderRadius: '10px', border: '1px solid #2c3539', marginBottom: '20px', boxSizing: 'border-box' };
  const labelStyle = { display: 'block', fontSize: '0.68rem', fontWeight: 'bold', color: '#95a5a6', marginBottom: '6px', letterSpacing: '0.3px' };
  const inputStyle = { width: '100%', padding: '10px', background: '#111416', border: '1px solid #34495e', borderRadius: '6px', color: '#fff', fontSize: '0.85rem', boxSizing: 'border-box' };

  useEffect(() => {
    const hours = parseFloat(hoursPostIngestion);
    const rawLevel = parseFloat(measuredLevel);
    const weightNum = parseFloat(weight);

    // Validation Guard: Weight and Level are always mandatory
    if (isNaN(rawLevel) || rawLevel <= 0 || isNaN(weightNum) || weightNum <= 0) {
      setTriage(prev => ({
        ...prev,
        statusLabel: 'AWAITING METRICS',
        color: '#7f8c8d',
        bgColor: 'rgba(127, 140, 141, 0.1)',
        normalizedMgL: 0,
        thresholdMgL: 0,
        isToxic: false,
        nacDosingPlan: []
      }));
      return;
    }

    // 1. Normalize Input Units to mg/L
    const normalizedMgL = unit === 'umol/L' ? rawLevel / 6.615 : rawLevel;

    // Initialize tracking variables
    let statusTxt = 'PROCESSED';
    let statusClr = '#2ecc71';
    let statusBg = 'rgba(46, 204, 113, 0.15)';
    let plan = '';
    let thresholdMgL = 0;
    let crossTreatmentLine = false;

    // =========================================================
    // BRANCH A: REPEATED SUPRA-THERAPEUTIC CHRONIC INGESTION
    // =========================================================
    if (overdoseType === 'chronic') {
      // Determine threshold boundary based on patient vulnerability stratification
      thresholdMgL = isHighRiskPatient ? 5.0 : 20.0;

      if (normalizedMgL > thresholdMgL) {
        statusTxt = '🚨 CHRONIC TOXICITY: START ANTIDOTE';
        statusClr = '#e74c3c';
        statusBg = 'rgba(231, 76, 60, 0.15)';
        crossTreatmentLine = true;
        plan = `Patient serum level (${normalizedMgL.toFixed(1)} mg/L) exceeds the chronic ingestion ceiling cutoff of ${thresholdMgL} mg/L. Initiate a full 3-stage intravenous Acetylcysteine (NAC) infusion protocol immediately.`;
      } else {
        statusTxt = '🟢 CHRONIC INGESTION: SAFE OUTCOME';
        statusClr = '#2ecc71';
        statusBg = 'rgba(46, 204, 113, 0.15)';
        plan = `Patient serum level (${normalizedMgL.toFixed(1)} mg/L) is below the toxicity cutoff ceiling of ${thresholdMgL} mg/L. Risk of hepatotoxicity is low. Continue monitoring liver profile trends if clinically symptomatic.`;
      }
    } 
    
    // =========================================================
    // BRANCH B: ACUTE SINGLE OVERDOSE PATHWAY (Nomogram Logic)
    // =========================================================
    else {
      if (isNaN(hours) || hours < 0) {
        setTriage(prev => ({ ...prev, statusLabel: 'AWAITING TIME ANCHOR' }));
        return;
      }

      if (hours < 4) {
        statusTxt = '⚠️ INVALID TIMELINE (TOO EARLY)';
        statusClr = '#3498db';
        statusBg = 'rgba(52, 152, 219, 0.15)';
        plan = 'Blood drawn under 4 hours post-acute ingestion is unreliable due to active gastrointestinal absorption phases. Keep monitored and redraw serum paracetamol assay exactly at the 4-hour mark.';
      } else if (hours > 24) {
        // Late or Delayed Presentation criteria from tdm9.jpg
        thresholdMgL = isHighRiskPatient ? 5.0 : 10.0;
        
        if (normalizedMgL > thresholdMgL) {
          statusTxt = '🚨 DELAYED PRESENTATION TOXICITY';
          statusClr = '#e74c3c';
          statusBg = 'rgba(231, 76, 60, 0.15)';
          crossTreatmentLine = true;
          plan = `Presentation exceeds 24 hours. Level (${normalizedMgL.toFixed(1)} mg/L) exceeds the late presentation safety floor of ${thresholdMgL} mg/L. Start Acetylcysteine (NAC) antidote immediately.`;
        } else {
          statusTxt = '🟢 DELAYED PRESENTATION: SUB-TOXIC';
          statusClr = '#2ecc71';
          statusBg = 'rgba(46, 204, 113, 0.15)';
          plan = `Presentation exceeds 24 hours, but level (${normalizedMgL.toFixed(1)} mg/L) is below the late presentation threshold of ${thresholdMgL} mg/L. Maintain supportive surveillance.`;
        }
      } else {
        // Standard Nomogram Window (4 to 24 Hours)
        thresholdMgL = 150 * Math.exp(-0.1732 * (hours - 4));

        if (normalizedMgL >= thresholdMgL) {
          statusTxt = '🚨 ACUTE TOXICITY: START ANTIDOTE';
          statusClr = '#e74c3c';
          statusBg = 'rgba(231, 76, 60, 0.15)';
          crossTreatmentLine = true;
          plan = `Patient concentration (${normalizedMgL.toFixed(1)} mg/L) sits ABOVE the treatment line (${thresholdMgL.toFixed(1)} mg/L). Start IV Acetylcysteine (NAC) immediately.`;
        } else {
          statusTxt = '🟢 ACUTE INGESTION: SAFE ZONE';
          statusClr = '#2ecc71';
          statusBg = 'rgba(46, 204, 113, 0.15)';
          plan = `Patient concentration (${normalizedMgL.toFixed(1)} mg/L) is safely below the treatment ceiling line. Toxicity risk is low.`;
        }
      }
    }

    // --- 3. THREE-STAGE INFUSION CALCULATION MATRIX ---
    let stages = [];
    if (crossTreatmentLine) {
      if (weightNum < 20) {
        stages = [
          { stage: '1. Loading Dose', doseMg: 150 * weightNum, fluidVol: 3 * weightNum, fluidType: '5% Dextrose', time: '60 minutes' },
          { stage: '2. Second Stage', doseMg: 50 * weightNum, fluidVol: 7 * weightNum, fluidType: '5% Dextrose', time: '4 hours' },
          { stage: '3. Third Stage', doseMg: 100 * weightNum, fluidVol: 14 * weightNum, fluidType: '5% Dextrose', time: '16 hours' }
        ];
      } else if (weightNum >= 20 && weightNum <= 50) {
        stages = [
          { stage: '1. Loading Dose', doseMg: 150 * weightNum, fluidVol: 100, fluidType: '5% Dextrose', time: '60 minutes' },
          { stage: '2. Second Stage', doseMg: 50 * weightNum, fluidVol: 250, fluidType: '5% Dextrose', time: '4 hours' },
          { stage: '3. Third Stage', doseMg: 100 * weightNum, fluidVol: 500, fluidType: '5% Dextrose', time: '16 hours' }
        ];
      } else {
        stages = [
          { stage: '1. Loading Dose', doseMg: 150 * weightNum, fluidVol: 200, fluidType: '5% Dextrose', time: '60 minutes' },
          { stage: '2. Second Stage', doseMg: 50 * weightNum, fluidVol: 500, fluidType: '5% Dextrose', time: '4 hours' },
          { stage: '3. Third Stage', doseMg: 100 * weightNum, fluidVol: 1000, fluidType: '5% Dextrose', time: '16 hours' }
        ];
      }
    }

    setTriage({
      statusLabel: statusTxt,
      color: statusClr,
      bgColor: statusBg,
      normalizedMgL,
      thresholdMgL,
      isToxic: crossTreatmentLine,
      nacDosingPlan: stages,
      actionPlan: plan
    });

  }, [overdoseType, hoursPostIngestion, measuredLevel, unit, weight, isHighRiskPatient]);

  // Helper to map patient pinpoint coordinates onto the SVG canvas layout grid
  const renderPlotNode = () => {
    const h = parseFloat(hoursPostIngestion);
    const lvl = triage.normalizedMgL;
    if (isNaN(h) || h < 4 || h > 24 || isNaN(lvl) || lvl <= 0) return null;

    const xPixel = 60 + ((h - 4) / 20) * 400;
    const yPixel = 300 - (Math.min(300, lvl) / 300) * 280;

    return (
      <g>
        <circle cx={xPixel} cy={yPixel} r="9" fill={triage.color} opacity="0.4">
          <animate attributeName="r" values="6;11;6" dur="1.8s" repeatCount="indefinite" />
          <animate attributeName="opacity" values="0.5;0.1;0.5" dur="1.8s" repeatCount="indefinite" />
        </circle>
        <circle cx={xPixel} cy={yPixel} r="5" fill="#fff" stroke={triage.color} strokeWidth="3" />
        <text x={xPixel + 8} y={yPixel - 8} fill="#fff" fontSize="10" fontWeight="bold" style={{ textShadow: '0px 1px 3px rgba(0,0,0,0.9)' }}>
          Patient ({h}h, {lvl.toFixed(1)} mg/L)
        </text>
      </g>
    );
  };

  // Pre-generate the geometric vector path array for the treatment line
  let mtxCurvePath = "";
  for (let hr = 4; hr <= 24; hr += 0.5) {
    const x = 60 + ((hr - 4) / 20) * 400;
    const yVal = 150 * Math.exp(-0.1732 * (hr - 4));
    const y = 300 - (yVal / 300) * 280;
    mtxCurvePath += `${hr === 4 ? 'M' : 'L'} ${x} ${y} `;
  }

  return (
    <div style={{ padding: '20px', background: '#0e1013', minHeight: '100vh', color: '#ecf0f1', fontFamily: 'system-ui, -apple-system' }}>
      <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: '10px' }}>
        <button 
          type="button"
          className="no-print" 
          onClick={() => {
                          const actualUserEmail = user && user.email ? user.email : 'anonymous-user';
                          const trackingLabel = `${actualUserEmail}-printed-Paracetamol`;
                          logUserActivityQuietly(trackingLabel); 
                          window.print();
                      }}
          style={{
            padding: '10px 20px',
            backgroundColor: '#27ae60', 
            color: 'white',
            border: 'none',
            borderRadius: '6px',
            cursor: 'pointer',
            fontWeight: 'bold',
            fontSize: '0.9rem',
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
            boxShadow: '0 4px 6px rgba(0,0,0,0.2)'
          }}
        >
          🖨️ Print PDF Report
        </button>
      </div>
      {/* HEADER TITLE PANEL */}
      <div style={{ textAlign: 'center', marginBottom: '25px', borderBottom: '2px solid #2ecc71', paddingBottom: '15px' }}>
        <h2 style={{ margin: 0, fontSize: '1.8rem', color: '#2ecc71', fontWeight: '800' }}>PARACETAMOL TOXICITY TRIAGE</h2>
        <span style={{ fontSize: '0.8rem', color: '#95a5a6', textTransform: 'uppercase' }}>BSA & Weight-Calibrated Antidote Resuscitation Panel</span>
      </div>
      {/* DROPDOWN 1: THERAPEUTIC TARGET RANGES (tdm10.png & tdm9.jpg) */}
      <details style={{ background: '#1c2226', border: '1px solid #34495e', borderRadius: '8px', padding: '12px', cursor: 'pointer' }}>
        <summary style={{ fontSize: '0.82rem', fontWeight: 'bold', color: '#e74c3c', outline: 'none', userSelect: 'none' }}>
          📊 View Acceptable Therapeutic Windows & Toxicity Ranges
        </summary>
        <div style={{ marginTop: '12px', borderTop: '1px solid rgba(255,255,255,0.05)', paddingTop: '10px', cursor: 'default' }} onClick={(e) => e.stopPropagation()}>
          
          {/* ACUTE TOXICITY HEADERS */}
          <h5 style={{ color: '#e74c3c', margin: '0 0 8px 0', fontSize: '0.72rem', textTransform: 'uppercase', letterSpacing: '0.3px' }}>
            Acute / Staggered Overdosing Pathway (Rumack-Matthew Nomogram Rules):
          </h5>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(130px, 1fr))', gap: '10px', marginBottom: '15px' }}>
            <div style={{ background: '#111416', padding: '8px 12px', borderRadius: '4px' }}>
              <span style={{ fontSize: '0.62rem', color: '#7f8c8d', display: 'block' }}>POST 4 HOURS:</span>
              <strong style={{ fontSize: '0.85rem', color: '#fff' }}>150 <span style={{ fontSize: '0.65rem', color: '#7f8c8d', fontWeight: 'normal' }}>mg/L</span></strong>
              <span style={{ fontSize: '0.55rem', color: '#7f8c8d', display: 'block' }}>(1000 µmol/L)</span>
            </div>
            <div style={{ background: '#111416', padding: '8px 12px', borderRadius: '4px' }}>
              <span style={{ fontSize: '0.62rem', color: '#7f8c8d', display: 'block' }}>POST 24 HOURS:</span>
              <strong style={{ fontSize: '0.85rem', color: '#fff' }}>4.7 <span style={{ fontSize: '0.65rem', color: '#7f8c8d', fontWeight: 'normal' }}>mg/L</span></strong>
              <span style={{ fontSize: '0.55rem', color: '#7f8c8d', display: 'block' }}>(30 µmol/L)</span>
            </div>
            <div style={{ background: '#111416', padding: '8px 12px', borderRadius: '4px' }}>
              <span style={{ fontSize: '0.62rem', color: '#7f8c8d', display: 'block' }}>UNKNOWN / DELAYED &gt;24H:</span>
              <strong style={{ fontSize: '0.85rem', color: '#ff7675' }}>Toxic if &gt; 10 <span style={{ fontSize: '0.65rem', fontWeight: 'normal' }}>mg/L</span></strong>
              <span style={{ fontSize: '0.55rem', color: '#7f8c8d', display: 'block' }}>(&gt; 5 mg/L if high-risk)</span>
            </div>
          </div>

          {/* NEW CHRONIC INGESTION MATRIX BLOCK */}
          <h5 style={{ color: '#f1c40f', margin: '0 0 8px 0', fontSize: '0.72rem', textTransform: 'uppercase', letterSpacing: '0.3px' }}>
            Repeated Supra-Therapeutic Ingestion (Chronic Ingestion Pathway):
          </h5>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px', marginBottom: '10px' }}>
            <div style={{ background: '#111416', padding: '10px', borderRadius: '6px', borderLeft: '3px solid #f1c40f' }}>
              <span style={{ fontSize: '0.62rem', color: '#7f8c8d', display: 'block' }}>STANDARD TOXIC FLOOR:</span>
              <strong style={{ fontSize: '1rem', color: '#fff' }}>&gt; 20 mg/L</strong>
            </div>
            <div style={{ background: '#111416', padding: '10px', borderRadius: '6px', borderLeft: '3px solid #e67e22' }}>
              <span style={{ fontSize: '0.62rem', color: '#7f8c8d', display: 'block' }}>HIGH-RISK HEPATOTOXIC PROFILE FLOOR:</span>
              <strong style={{ fontSize: '1rem', color: '#ff7675' }}>&gt; 5 mg/L</strong>
              <span style={{ fontSize: '0.55rem', color: '#7f8c8d', display: 'block', marginTop: '2px' }}>(Malnourished, chronic alcohol dependency, enzyme-inducing therapies)</span>
            </div>
          </div>
        </div>
      </details>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(320px, 1fr))', gap: '20px' }}>
        
        {/* LEFT COLUMN: CRITICAL PATIENT DATA ENTRY */}
        <div>
          <div style={cardStyle}>
            <h3 style={{ color: '#2ecc71', margin: '0 0 15px 0', fontSize: '1.05rem', textTransform: 'uppercase' }}>
              ⏱️ Step 1: Exposure Metrics & Biometrics
            </h3>

            {/* OVERDOSE TYPE SELECTION DROPDOWN */}
            <div style={{ marginBottom: '15px' }}>
              <label style={labelStyle}>OVERDOSE INGESTION PATTERN PROFILE:</label>
              <select value={overdoseType} onChange={(e) => setOverdoseType(e.target.value)} style={inputStyle}>
                <option value="acute">Acute Single Overdose / Staggered Ingestion</option>
                <option value="chronic">Repeated Supra-Therapeutic Ingestion (Chronic Ingestion)</option>
              </select>
            </div>
            
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '15px', marginBottom: '15px' }}>
              <div>
                <label style={labelStyle}>PATIENT WEIGHT (KG):</label>
                <input type="number" step="0.1" placeholder="e.g. 65 kg" value={weight} onChange={(e) => setWeight(e.target.value)} style={inputStyle} />
              </div>
              <div>
                {overdoseType === 'acute' ? (
                  <>
                    <label style={labelStyle}>HOURS SINCE ACUTE EXPOSURE:</label>
                    <input type="number" step="0.5" placeholder="e.g. 6.5h" value={hoursPostIngestion} onChange={(e) => setHoursPostIngestion(e.target.value)} style={inputStyle} />
                  </>
                ) : (
                  <div style={{ background: '#111416', padding: '10px', borderRadius: '6px', border: '1px dashed #f1c40f', height: '100%', boxSizing: 'border-box', display: 'flex', alignItems: 'center' }}>
                    <span style={{ fontSize: '0.65rem', color: '#f1c40f', fontStyle: 'italic', lineHeight: '1.3' }}>
                      ⏱️ Chronic Ingestion Rule: Sample immediately upon admission.
                    </span>
                  </div>
                )}
              </div>
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: '1.2fr 0.8fr', gap: '10px', marginBottom: '15px' }}>
              <div>
                <label style={labelStyle}>REPORTED SERUM CONCENTRATION:</label>
                <input 
                  type="number" 
                  step="0.1" 
                  placeholder="Type lab assay value" 
                  value={measuredLevel} 
                  onChange={(e) => setMeasuredLevel(e.target.value)} 
                  style={{ ...inputStyle, border: measuredLevel ? `1px solid ${triage.color}` : '1px solid #34495e' }} 
                />
              </div>
              <div>
                <label style={labelStyle}>METRIC UNIT:</label>
                <select value={unit} onChange={(e) => setUnit(e.target.value)} style={inputStyle}>
                  <option value="mg/L">mg/L (mcg/mL)</option>
                  <option value="umol/L">µmol/L</option>
                </select>
              </div>
            </div>

            {/* HIGH RISK STRATIFICATION INTERACTIVE SELECTION CHECKBOX */}
            <div style={{ background: 'rgba(230, 126, 34, 0.05)', border: '1px solid rgba(230, 126, 34, 0.2)', padding: '12px', borderRadius: '6px', display: 'flex', alignItems: 'center', gap: '10px' }}>
              <input 
                type="checkbox" 
                id="highRiskCheck"
                checked={isHighRiskPatient} 
                onChange={(e) => setIsHighRiskPatient(e.target.checked)}
                style={{ width: '16px', height: '16px', cursor: 'pointer' }}
              />
              <label htmlFor="highRiskCheck" style={{ fontSize: '0.72rem', color: '#e67e22', fontWeight: 'bold', cursor: 'pointer', userSelect: 'none', lineHeight: '1.3' }}>
                ⚠️ Patient exhibits High-Risk Hepatotoxic Profile Indicators<br />
                <span style={{ fontWeight: 'normal', color: '#95a5a6', fontSize: '0.65rem' }}>(Malnourishment, chronic alcohol misuse, or on CYP3A4-inducing medications)</span>
              </label>
            </div>
          </div>

          {/* TRIAGE ACTION ALERT HEADER PANEL */}
          <div style={{ 
            background: triage.bgColor, 
            border: `1px solid ${triage.color}`, 
            color: triage.color, 
            padding: '15px', 
            borderRadius: '10px', 
            marginBottom: '20px', 
            textAlign: 'center'
          }}>
            <span style={{ fontSize: '0.62rem', display: 'block', letterSpacing: '1px', marginBottom: '2px', opacity: 0.8 }}>CRITICAL TOXICOLOGY ASSESSMENT</span>
            <strong style={{ fontSize: '1.15rem', fontWeight: '900' }}>{triage.statusLabel}</strong>
          </div>

          <div style={{ ...cardStyle, borderLeft: `4px solid ${triage.color}` }}>
            <span style={{ display: 'block', fontSize: '0.72rem', color: '#3498db', fontWeight: 'bold', marginBottom: '6px', textTransform: 'uppercase' }}>
              📋 Pharmacist Clinical Assessment:
            </span>
            <p style={{ margin: 0, fontSize: '0.85rem', color: '#bdc3c7', lineHeight: '1.5', textAlign: 'left' }}>
              {triage.actionPlan}
            </p>
          </div>
        </div>

        {/* RIGHT COLUMN: LIVE GRAPH OVERLAY & ANTIDOTE INFUSION TRACKER */}
        <div>
          {/* INTERACTIVE NOMOGRAM COMPONENT VECTOR MAP */}
          <div style={{ ...cardStyle, padding: '15px' }}>
            <h3 style={{ color: '#fff', margin: '0 0 10px 0', fontSize: '0.95rem', textTransform: 'uppercase', textAlign: 'center' }}>
              📊 Live Rumack-Matthew Overlay Chart
            </h3>
            <div style={{ background: '#111416', borderRadius: '8px', padding: '10px', border: '1px solid #2c3539' }}>
              <svg viewBox="0 0 500 350" style={{ width: '100%', height: 'auto', overflow: 'visible' }}>
                <line x1="60" y1="20" x2="460" y2="20" stroke="rgba(255,255,255,0.04)" strokeDasharray="3" />
                <line x1="60" y1="113" x2="460" y2="113" stroke="rgba(255,255,255,0.04)" strokeDasharray="3" />
                <line x1="60" y1="206" x2="460" y2="206" stroke="rgba(255,255,255,0.04)" strokeDasharray="3" />
                
                <line x1="60" y1="20" x2="60" y2="300" stroke="#4b5563" strokeWidth="2" />
                <line x1="60" y1="300" x2="460" y2="300" stroke="#4b5563" strokeWidth="2" />

                <path d={`${mtxCurvePath} L 460 300 L 60 300 Z`} fill="rgba(46, 204, 113, 0.04)" />
                <path d={`M 60 20 L 460 20 L 460 300 ${mtxCurvePath.split(' ').reverse().join(' ')} Z`} fill="rgba(231, 76, 60, 0.03)" />
                <path d={mtxCurvePath} fill="none" stroke="#e74c3c" strokeWidth="3" strokeDasharray="1" />

                {/* CHART TEXT LABELS */}
                <text x="260" y="335" fill="#9ca3af" fontSize="11" textAnchor="middle" fontWeight="bold">Interval Since Acute Exposure (Hours)</text>
                <text x="18" y="160" fill="#9ca3af" fontSize="11" textAnchor="middle" transform="rotate(-90 18 160)" fontWeight="bold">Plasma Level (mg/L)</text>

                {/* TICK MARK INTERVAL MARKS */}
                <text x="60" y="315" fill="#6b7280" fontSize="9" textAnchor="middle">4h</text>
                <text x="160" y="315" fill="#6b7280" fontSize="9" textAnchor="middle">9h</text>
                <text x="260" y="315" fill="#6b7280" fontSize="9" textAnchor="middle">14h</text>
                <text x="360" y="315" fill="#6b7280" fontSize="9" textAnchor="middle">19h</text>
                <text x="460" y="315" fill="#6b7280" fontSize="9" textAnchor="middle">24h</text>

                <text x="52" y="303" fill="#6b7280" fontSize="9" textAnchor="end">0</text>
                <text x="52" y="209" fill="#6b7280" fontSize="9" textAnchor="end">100</text>
                <text x="52" y="116" fill="#6b7280" fontSize="9" textAnchor="end">200</text>
                <text x="52" y="24" fill="#6b7280" fontSize="9" textAnchor="end">300</text>

                {/* EXACT MATCH DICTIONARY TO HANDBOOK NOMOGRAM WORDS (tdm7.png) */}
                <text x="75" y="145" fill="#e74c3c" fontSize="9" fontWeight="bold">150 mg/L Treatment Line</text>
                <text x="240" y="100" fill="#ff7675" fontSize="12" fontWeight="800" letterSpacing="0.5px" opacity="0.8">Probable hepatic toxicity</text>
                <text x="240" y="240" fill="#2ecc71" fontSize="12" fontWeight="800" letterSpacing="0.5px" opacity="0.8">No hepatic toxicity</text>

                {renderPlotNode()}
              </svg>
            </div>
            <div style={{ fontSize: '0.65rem', color: '#a4b0be', marginTop: '8px', textAlign: 'left', lineHeight: '1.4', fontStyle: 'italic', borderTop: '1px dashed rgba(255,255,255,0.05)', paddingTop: '6px' }}>
              <strong>* Clinical Remark on Safety Cushion:</strong> This chart plots the standard 150-mg/L treatment line, which incorporates a built-in 25% safety cushion below the original 1975 Rumack-Matthew mathematical toxicity line (starting at 200 mg/L at 4 hours). By using the 150-line as an absolute cutoff protocol, the engine automatically triggers the Acetylcysteine (NAC) infusion pathway at this conservative threshold to protect against errors in ingestion timeline recall or laboratory assay variances.
            </div>
          </div>

          {/* DYNAMIC THREE STAGE NAC DOSING INFUSION CARD (tdm8.png) */}
          {triage.isToxic && triage.nacDosingPlan.length > 0 && (
            <div style={{ ...cardStyle, borderLeft: '4px solid #9b59b6', padding: '15px' }}>
              <span style={{ display: 'block', fontSize: '0.72rem', color: '#9b59b6', fontWeight: 'bold', marginBottom: '10px', textTransform: 'uppercase' }}>
                🧬 Intravenous NAC Three-Stage Infusion Protocol Grid
              </span>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                {triage.nacDosingPlan.map((s, index) => (
                  <div key={index} style={{ background: '#111416', padding: '10px', borderRadius: '6px', fontSize: '0.78rem', border: '1px solid rgba(255,255,255,0.03)' }}>
                    <strong style={{ color: '#9b59b6', display: 'block', marginBottom: '2px' }}>{s.stage}</strong>
                    <div style={{ color: '#fff', lineHeight: '1.4' }}>
                      • Antidote Payload: <strong style={{ color: '#00d2ff' }}>{s.doseMg.toLocaleString()} mg</strong> Acetylcysteine<br />
                      • Fluid Vehicle Parameters: Administer in <strong style={{ color: '#f1c40f' }}>{s.fluidVol} mL</strong> of {s.fluidType}<br />
                      • Infusion Run Duration: Run continuously over <strong style={{ color: '#2ecc71' }}>{s.time}</strong>
                    </div>
                  </div>
                ))}
              </div>
              <span style={{ display: 'block', fontSize: '0.62rem', color: '#e74c3c', marginTop: '10px', fontStyle: 'italic' }}>
                *Dosing parameters optimized for recorded patient weight category: {weight} kg. Total protocol run duration: 21 hours total.
              </span>
            </div>
          )}
        </div>

      </div>

      {/* FOOTER GENERAL LEGEND STATEMENT */}
      <p style={{ margin: '20px 0 0 0', fontSize: '0.62rem', color: '#7f8c8d', fontStyle: 'italic', textAlign: 'center', lineHeight: '1.3' }}>
        *Evaluation derived from official Rumack-Matthew Toxicology Nomogram criteria and Three-Stage Intravenous Acetylcysteine (NAC) guidelines. Patient-specific absolute clinical metrics, physical presentations, and toxicological center mandates must override automated profiles prior to treatment deployment.
      </p>

    </div>
  );
}